/**
 * Admin layout with side panel.
 * URL-driven sections: /admin/users, /admin/analytics, /admin/settings
 *
 * The left rail collapses to icon-only via the chevron in its header. The
 * collapsed state is persisted to localStorage so it survives reloads.
 * Animation uses MUI's standard "sharp" transition curve, ~220 ms.
 */
import { Box, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Paper, Tooltip, Typography } from '@mui/material';
import { People, BarChart, Settings, ListAlt, BugReport, Tune, DescriptionOutlined, ChevronLeft, ChevronRight, EventNote, CurrencyExchange, LockOutlined, Hub, FactCheckOutlined, FunctionsOutlined, HistoryOutlined, MarkEmailReadOutlined, InboxOutlined } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AdminUsersPage } from './AdminUsersPage';
import { AnalyticsDashboard } from './AnalyticsDashboard';
import { ChatSettingsPanel } from './ChatSettingsPanel';
import { TestCatalogPage } from './testCatalog/TestCatalogPage';
import { QuotePermissionsPage } from './quotePermissions/QuotePermissionsPage';
import { AdminFeedbackPage } from './feedback/AdminFeedbackPage';
import { SieveNestsPage } from './sieveNests/SieveNestsPage';
import { ReportTemplatesPage } from './reportTemplates/ReportTemplatesPage';
import { StatHolidaysPage } from './statHolidays/StatHolidaysPage';
import { ExchangeRatesPage } from './exchangeRates/ExchangeRatesPage';
import { TestMappingPage } from './testMapping/TestMappingPage';
import { AppIntegrityPage } from './appIntegrity/AppIntegrityPage';
import { TemplateVersionsPage } from './templateVersions/TemplateVersionsPage';
import { FormulaVersionsPage } from './formulaVersions/FormulaVersionsPage';
import { IntakeFormSettingsPage } from './intakeForm/IntakeFormSettingsPage';
import { EmailNotificationsPage } from './emailNotifications/EmailNotificationsPage';

const SECTIONS = [
  { key: 'users', label: 'Users', icon: <People />, description: 'Manage user roles and access', path: '/admin/users' },
  { key: 'analytics', label: 'Analytics', icon: <BarChart />, description: 'Login history, usage stats', path: '/admin/analytics' },
  { key: 'settings', label: 'AI Settings', icon: <Settings />, description: 'Prompt, tools, model config', path: '/admin/settings' },
  { key: 'test-catalog', label: 'Test Catalogue', icon: <ListAlt />, description: 'Manage test price list', path: '/admin/test-catalog' },
  { key: 'test-mapping', label: 'Test Mapping', icon: <Hub />, description: 'Link lab tests to scheduling catalog', path: '/admin/test-mapping' },
  { key: 'app-integrity', label: 'App Integrity', icon: <FactCheckOutlined />, description: 'E2E golden-test validation', path: '/admin/app-integrity' },
  { key: 'formula-versions', label: 'Formula Versions', icon: <FunctionsOutlined />, description: 'Formula Ledger — change history & audit trail', path: '/admin/formula-versions' },
  { key: 'sieve-nests', label: 'Sieve Sets', icon: <Tune />, description: 'PSD sieve nest catalog', path: '/admin/sieve-nests' },
  // RBAC Phase B (2026-08-06): dollar-tier approval retired — approval is admin-only, every External quote.
  { key: 'quote-permissions', label: 'Quote Permissions', icon: <LockOutlined />, description: 'Who can create quotes', path: '/admin/quote-permissions' },
  { key: 'intake-form', label: 'Intake Form', icon: <InboxOutlined />, description: 'Public client intake link contents', path: '/admin/intake-form' },
  { key: 'email-notifications', label: 'Email Notifications', icon: <MarkEmailReadOutlined />, description: 'Event emails & recipients', path: '/admin/email-notifications' },
  { key: 'report-templates', label: 'Report Header & Footer', icon: <DescriptionOutlined />, description: 'Shared header/footer across all PDF reports', path: '/admin/report-templates' },
  { key: 'template-versions', label: 'Template Versions', icon: <HistoryOutlined />, description: 'Per-report PDF template versions & history', path: '/admin/template-versions' },
  { key: 'feedback', label: 'Feedback', icon: <BugReport />, description: 'Bug reports & feature requests', path: '/admin/feedback' },
  { key: 'stat-holidays', label: 'Stat Holidays', icon: <EventNote />, description: 'Public holidays for scheduling', path: '/admin/stat-holidays' },
  { key: 'exchange-rates', label: 'Exchange Rates', icon: <CurrencyExchange />, description: 'FX-rate periods for quote locking', path: '/admin/exchange-rates' },
] as const;

type SectionKey = typeof SECTIONS[number]['key'];

const RAIL_EXPANDED  = 240;
const RAIL_COLLAPSED = 56;
const STORAGE_KEY    = 'belab.admin.rail.collapsed';
/** MUI standard "sharp" transition. Match it on every animated property
 *  so they all share the same easing + duration and the rail glides as
 *  one element. */
const TRANSITION = 'all 220ms cubic-bezier(0.4, 0, 0.2, 1)';

export const AdminLayout = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const active: SectionKey = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/admin/analytics')) return 'analytics';
    if (path.startsWith('/admin/settings')) return 'settings';
    if (path.startsWith('/admin/test-catalog')) return 'test-catalog';
    if (path.startsWith('/admin/test-mapping')) return 'test-mapping';
    if (path.startsWith('/admin/app-integrity')) return 'app-integrity';
    if (path.startsWith('/admin/formula-versions')) return 'formula-versions';
    if (path.startsWith('/admin/sieve-nests')) return 'sieve-nests';
    if (path.startsWith('/admin/quote-permissions')) return 'quote-permissions';
    if (path.startsWith('/admin/intake-form')) return 'intake-form';
    if (path.startsWith('/admin/email-notifications')) return 'email-notifications';
    if (path.startsWith('/admin/template-versions')) return 'template-versions';
    if (path.startsWith('/admin/report-templates')) return 'report-templates';
    if (path.startsWith('/admin/feedback')) return 'feedback';
    if (path.startsWith('/admin/stat-holidays')) return 'stat-holidays';
    if (path.startsWith('/admin/exchange-rates')) return 'exchange-rates';
    return 'users';
  }, [location.pathname]);

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  return (
    // Fixed-height shell so the left rail stays docked. The outer row exactly
    // fills the viewport below the app bar (60px) minus AppShell's vertical
    // padding (2×16px) and never scrolls itself — only the content column
    // scrolls. We avoid position:sticky because AppShell's <main> sets
    // overflowX:hidden, which makes it a scroll container and would break
    // window-relative sticky on the rail.
    <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 92px)', overflow: 'hidden' }}>
      <Paper
        variant="outlined"
        sx={{
          width: collapsed ? RAIL_COLLAPSED : RAIL_EXPANDED,
          flexShrink: 0,
          borderRadius: 2,
          borderColor: alpha(theme.palette.divider, 0.5),
          // Clip horizontally during the collapse animation; scroll vertically
          // when the section list is taller than a short viewport.
          overflowX: 'hidden',
          overflowY: 'auto',
          transition: TRANSITION,
        }}
      >
        <Box sx={{
          px: collapsed ? 0.5 : 2, py: 1.25,
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between',
          transition: TRANSITION,
        }}>
          {!collapsed && (
            <Typography variant="subtitle2" sx={{
              fontWeight: 700, fontSize: '0.85rem', color: 'text.primary',
              opacity: collapsed ? 0 : 1, transition: TRANSITION,
            }}>
              Administration
            </Typography>
          )}
          <Tooltip title={collapsed ? 'Expand' : 'Collapse'} placement="right">
            <IconButton size="small" onClick={() => setCollapsed((c) => !c)}>
              {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        <List disablePadding>
          {SECTIONS.map((s) => {
            const button = (
              <ListItemButton
                key={s.key}
                selected={active === s.key}
                onClick={() => navigate(s.path)}
                sx={{
                  py: 1.25,
                  px: collapsed ? 1 : 2,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  transition: TRANSITION,
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    borderRight: `3px solid ${theme.palette.primary.main}`,
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
                  },
                }}>
                <ListItemIcon sx={{
                  minWidth: collapsed ? 0 : 36,
                  color: active === s.key ? 'primary.main' : 'text.secondary',
                  justifyContent: 'center',
                  transition: TRANSITION,
                }}>
                  {s.icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText primary={s.label} secondary={s.description}
                    primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: active === s.key ? 600 : 400 }}
                    secondaryTypographyProps={{ fontSize: '0.65rem' }}
                    sx={{ opacity: collapsed ? 0 : 1, transition: TRANSITION }}
                  />
                )}
              </ListItemButton>
            );
            return collapsed ? (
              <Tooltip key={s.key} title={s.label} placement="right">
                <Box>{button}</Box>
              </Tooltip>
            ) : button;
          })}
        </List>
      </Paper>

      <Box sx={{ flex: 1, minWidth: 0, height: '100%', overflowY: 'auto' }}>
        {active === 'users' && <AdminUsersPage />}
        {active === 'analytics' && <AnalyticsDashboard />}
        {active === 'settings' && <ChatSettingsPanel />}
        {active === 'test-catalog' && <TestCatalogPage />}
        {active === 'test-mapping' && <TestMappingPage />}
        {active === 'app-integrity' && <AppIntegrityPage />}
        {active === 'formula-versions' && <FormulaVersionsPage />}
        {active === 'sieve-nests' && <SieveNestsPage />}
        {active === 'quote-permissions' && <QuotePermissionsPage />}
        {active === 'intake-form' && <IntakeFormSettingsPage />}
        {active === 'email-notifications' && <EmailNotificationsPage />}
        {active === 'report-templates' && <ReportTemplatesPage />}
        {active === 'template-versions' && <TemplateVersionsPage />}
        {active === 'feedback' && <AdminFeedbackPage />}
        {active === 'stat-holidays' && <StatHolidaysPage />}
        {active === 'exchange-rates' && <ExchangeRatesPage />}
      </Box>
    </Box>
  );
};
